import CloseIcon from 'theme/icons/CloseIcon';
import {
  borderRadius,
  boxShadows,
  colors,
  fontFamily,
  fontSize,
  fontWeight,
  gaps,
  padding,
} from 'theme/defaultStyle';
import React from 'react';

import styled from 'styled-components';
import toast from 'react-hot-toast';

import InfoIcon from './icons/InfoIcon';
import WarningIcon from './icons/WarningIcon';
import SuccessIcon from './icons/SuccessIcon';
import ErrorIcon from './icons/ErrorIcon';

interface ToastProps {
  t: { id: string };
  type: 'info' | 'error' | 'success' | 'warning'; // Type of the toast
  title?: string;
  message?: string | JSX.Element;
  onClose?: () => void; // Event handler for closing the toast
}

export type ToastType = 'info' | 'error' | 'success' | 'warning';

const toastStatusIconBackground = {
  gradients: {
    info: `linear-gradient(180deg, ${colors.blue14} 0%, ${colors.blue16} 100%)`,
    success: `linear-gradient(180deg, ${colors.green} 0%, ${colors.green01} 100%)`,
    warning: `linear-gradient(180deg, ${colors.yellow} 0%, ${colors.yellow01} 100%)`,
    error: `linear-gradient(180deg, ${colors.red01} 0%, ${colors.red03} 100%)`,
  },
};

const ToastContainer = styled.div`
  display: flex;
  /* width: 518px; */
  justify-content: center;
  align-items: center;
  font-family: ${fontFamily.inter}; 
  z-index: 999; /* Set the z-index value as per your requirement */
  box-shadow: ${boxShadows.toastShadow};
  justifycontent: flex-end;
  border-radius: ${borderRadius.large};
`;

const ToastLeft = styled.div`
  display: flex;
  width: 90px;
  padding: ${padding.medium2};
  justify-content: center;
  align-items: center;
  gap: ${gaps.small2};
  align-self: stretch;
  border-radius: ${borderRadius.large} ${borderRadius.zero} ${borderRadius.zero} ${borderRadius.large};
  background: ${({ type }: { type: ToastType }) => toastStatusIconBackground.gradients[type]};
`;

const ToastRight = styled.div`
  display: flex;
  min-width: 428px; 
  max-width: 428px; 
  gap: ${gaps.medium1};
  colum-gap: ${gaps.medium1};
  padding: ${padding.xlarge1};
  justify-content: space-between;
  align-items: center;
  border-radius: ${borderRadius.zero} ${borderRadius.large} ${borderRadius.large}
    ${borderRadius.zero};
  background: var(--token_neutral-f, #fff);
  cursor: pointer; /* Add cursor pointer to indicate it's clickable */
  position: relative; /* Position relative for absolute positioning of the close icon */
`;

const ToastBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${gaps.xsmall2};
  width: 340px;
`;

const CloseIconRight = styled(CloseIcon)`
  width: 24px;
  height: 24px;
  flex-shrink: 0;
  cursor: pointer; /* Add cursor pointer to indicate it's clickable */
`;

const ToastHeading = styled.h3`
  /* color: ${colors.grey08}; */
  color: ${colors.black01};
  /* Heading/ H3 : Semi Bold */
  font-family: ${fontFamily.inter};
  font-size: ${fontSize.xlarge0};
  font-style: normal;
  font-weight: ${fontWeight.bold1};
  line-height: normal;
  margin: 0px;
`;

const TaostMessage = styled('p')({
  color: colors.black01,
  fontFamily: fontFamily.inter,
  fontSize: fontSize.medium,
  fontStyle: "normal",
  fontWeight: fontWeight.normal1,
  lineHeight: "normal",  
  margin: 0,
});

const getToastIcon = (type: string) => {
  switch (type) {
    case 'error':
      return <ErrorIcon />;
    case 'success':
      return <SuccessIcon />;
    case 'warning':
      return <WarningIcon />;
    case 'info':
      return <InfoIcon />;
    default:
      return null;
  }
};

const StewartToast: React.FC<ToastProps> = ({ t, title, type, message, onClose }) => {
  const handleClose = () => {
    onClose && onClose();
    toast.remove(t.id); // Dismiss the toast
  };

  return (
    <ToastContainer>
      <ToastLeft type={type}>{getToastIcon(type)}</ToastLeft>
      <ToastRight onClick={onClose}>
        <ToastBody>
          {title && <ToastHeading>{title}</ToastHeading>}
          {message && <TaostMessage>{message}</TaostMessage>}
        </ToastBody>
        <CloseIconRight onClick={handleClose} />
      </ToastRight>
    </ToastContainer>
  );
};

export default StewartToast;
